import { getCodebookEntrySuggestions } from '@/api/generated/codebooks/codebooks';
import type { CodebookEntrySuggestion, ListRequest } from '@/api/generated/schemas';
import { createAsyncSelectorField } from '@/shared/forms';
import {
  defineEntityAsyncSelector,
  listRequestQueryKey,
  selectorPresets,
  toListRequest,
  type NoPickFilters,
} from '@/shared/ui/async-selector';

/** UI ref položky číselníku. */
export interface CodebookRef {
  id: number;
  name: string;
  code: string | null;
}

/**
 * Factory typového selektoru číselníku. Povinný parametr <c>folderIdentifier</c> (kategorie z tabulky
 * Folder, např. „StationType") vymezuje, které položky číselníku se nabízejí. Vrací typový selektor
 * (controlled) i form-bound field (TanStack Form).
 */
export function createCodebookSelector(folderIdentifier: string) {
  const selector = defineEntityAsyncSelector<
    CodebookRef,
    CodebookEntrySuggestion,
    ListRequest,
    NoPickFilters
  >({
    api: {
      fetch: getCodebookEntrySuggestions,
      buildApiParams: (ctx) =>
        toListRequest(ctx.search, ctx.ids, [{ field: 'folder', text: folderIdentifier }]),
      queryKey: (request) => listRequestQueryKey(`codebooks.${folderIdentifier}`, request),
      mapItem: (s) => ({ id: s.id, name: s.name ?? '', code: s.code ?? null }),
    },
    key: (item) => item.id,
    display: {
      kind: 'nameCode',
      name: (item) => item.name,
      code: (item) => item.code,
    },
    columns: (t) =>
      selectorPresets.nameAndCode({
        name: {
          id: 'name',
          header: t('codebookSelector.columns.name'),
          value: (item) => item.name,
          sortable: true,
        },
        code: {
          id: 'code',
          header: t('codebookSelector.columns.code'),
          value: (item) => item.code,
          emptyDash: '—',
        },
      }),
    i18n: { namespace: 'codebooks' },
  });

  return {
    Selector: selector.Selector,
    Field: createAsyncSelectorField(selector.Selector),
  };
}
